import {
    DEFAULT_SUPPORT_NODE,
    DEFAULT_SUPPORT_NODE_REGEX,
    NODE_OK_DEFAULT_TEXT
} from "../constants/parsingConstants";
import { getDefaultStore } from 'jotai';
import { chatbotConfigState } from '../../state/config/ChatbotConfigState';
import { TRANSLATIONS } from '../constants/translations';

function getActiveTranslation() {
    try {
        const store = getDefaultStore();
        const cfg = store.get(chatbotConfigState);
        const lang = (cfg?.language || 'de').toLowerCase().startsWith('en') ? 'en' : 'de';
        return TRANSLATIONS[lang] || TRANSLATIONS.de;
    } catch (e) {
        return TRANSLATIONS.de;
    }
}

function fmt(template, vars = {}) {
    return String(template || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : ''));
}

async function parseDrawioModel(rawXml) {
    function cleanCellValue(value) {
        if (!value) return "";
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(value, 'text/html');
            return doc.body.textContent.trim();
        } catch (e) {
            return value.replace(/<[^>]*>?/gm, '').trim();
        }
    }

    try {
        const xmlDoc = new DOMParser().parseFromString(rawXml, "text/xml");
        const parserError = xmlDoc.getElementsByTagName("parsererror")[0];
        if (parserError) {
            const t = getActiveTranslation();
            return { errors: [t.drawioXmlParserError] };
        }

        const allRoots = xmlDoc.getElementsByTagName("root");
        if (!allRoots || allRoots.length === 0) {
            const t = getActiveTranslation();
            return { errors: [t.drawioRootNotFound] };
        }

        const parsedCells = [];

        Array.from(allRoots).forEach((rootEl, pageIndex) => {
            const childEls = Array.from(rootEl.childNodes).filter(n => n.nodeType === Node.ELEMENT_NODE);

            for (const el of childEls) {
                let mxCellEl = el;
                let uoValue = null;
                let uoId = null;

                if (el.tagName === "UserObject") {
                    uoId = el.getAttribute("id");
                    uoValue = el.getAttribute("label") || el.getAttribute("value") || "";
                    mxCellEl = Array.from(el.childNodes).find(n => n.nodeType === Node.ELEMENT_NODE && n.tagName === "mxCell");
                    if (!mxCellEl) continue; 
                } else if (el.tagName !== "mxCell") {
                    continue;
                }

                const rawId = (uoId || mxCellEl.getAttribute("id")) ?? "";
                if (rawId === "0" || rawId === "1") continue; 

                const id = `p${pageIndex}_${rawId}`;
                const rawParent = mxCellEl.getAttribute("parent");
                const parentId = rawParent && rawParent !== "0" && rawParent !== "1" ? `p${pageIndex}_${rawParent}` : undefined;
                
                const rawSource = mxCellEl.getAttribute("source");
                const source = rawSource ? `p${pageIndex}_${rawSource}` : undefined;
                
                const rawTarget = mxCellEl.getAttribute("target");
                const target = rawTarget ? `p${pageIndex}_${rawTarget}` : undefined;

                const rawValue = uoValue !== null ? uoValue : (mxCellEl.getAttribute("value") ?? "");
                const value = cleanCellValue(rawValue);
                
                const style = mxCellEl.getAttribute("style") ?? "";
                const isVertex = mxCellEl.getAttribute("vertex") === "1";
                const isEdge = mxCellEl.getAttribute("edge") === "1";

                let mxGeometry = undefined;
                const geoEl = Array.from(mxCellEl.childNodes).find(n => n.nodeType === Node.ELEMENT_NODE && n.tagName === "mxGeometry");

                if (geoEl) {
                    mxGeometry = {
                        x: parseFloat(geoEl.getAttribute("x") || 0),
                        y: parseFloat(geoEl.getAttribute("y") || 0)
                    };
                }

                parsedCells.push({ cellId: id, parentId, cellValue: value, source, target, style, isVertex, isEdge, mxGeometry });
            }
        });

        return { model: { cells: parsedCells } };
    } catch (error) {
        const t = getActiveTranslation();
        return { errors: [fmt(t.drawioMalformed, { error: error.message })] };
    }
}

function extractSmartId(text, fallbackId) {
    if (!text) return fallbackId;
    const bracketMatch = text.match(/\[(.*?)\]/);
    if (bracketMatch) return bracketMatch[1].trim();
    const hashMatch = text.match(/#(\w+)/);
    if (hashMatch) return hashMatch[1].trim();
    return fallbackId;
}

function cleanTextForDisplay(text) {
    if (!text) return "";
    let clean = text.replace(/\[.*?\]/g, '').replace(/#\w+/g, '').trim();
    return clean || text;
}

export async function validateDrawioFile(fileContent) {
    const t = getActiveTranslation();
    const { model, errors: parseErrors } = await parseDrawioModel(fileContent);
    if (parseErrors) return { errors: parseErrors };

    const nodes = new Map();
    const edges = [];
    const labels = [];
    const childrenByParent = new Map();

    // 1. Categorize securely using explicit vertex and edge attributes
    model.cells.forEach(cell => {
        if (cell.parentId) {
            if (!childrenByParent.has(cell.parentId)) childrenByParent.set(cell.parentId, []);
            childrenByParent.get(cell.parentId).push(cell);
        }

        if (cell.isEdge) {
            edges.push(cell);
        } else if (cell.isVertex) {
            const valLower = (cell.cellValue || "").toLowerCase();
            if (cell.style && cell.style.includes("text") && valLower.includes("kategorie:")) {
                labels.push(cell);
            } else {
                nodes.set(cell.cellId, { ...cell, outEdges: [], inEdges: [] });
            }
        }
    });

    // 2. Connect the graph network
    edges.forEach(edge => {
        const sourceNode = nodes.get(edge.source);
        const targetNode = nodes.get(edge.target);
        if (sourceNode && targetNode) {
            sourceNode.outEdges.push(edge);
            targetNode.inEdges.push(edge);
        }
    });

    function resolveTargetId(targetNodeId) {
        if (!targetNodeId) return null;
        const targetNode = nodes.get(targetNodeId);
        if (!targetNode) return null;

        const hasText = !!cleanTextForDisplay(targetNode.cellValue);
        const hasImage = targetNode.style && targetNode.style.includes('image');
        
        if (hasText || hasImage) {
            return targetNode.cellId;
        }

        const children = childrenByParent.get(targetNode.cellId) || [];
        for (const child of children) {
            const resolved = resolveTargetId(child.cellId);
            if (resolved) return resolved;
        }

        return targetNode.cellId;
    }

    // 4. FIND VALID NODES - FIX: Removed ALL filters so EVERY shape/box is imported 
    // We no longer check if it has text or an image. If it is a box, we keep it.
    const validBotNodes = Array.from(nodes.values());
    
    let startNodes = validBotNodes.filter(n => n.inEdges.length === 0 && n.outEdges.length > 0);
    if (startNodes.length === 0) {
        startNodes = [...validBotNodes].sort((a, b) => (a.mxGeometry?.y || 0) - (b.mxGeometry?.y || 0));
    }

    if (startNodes.length === 0) {
        return { errors: [fmt(t.drawioStartQuestionCount, { count: 0 }) || "Es wurde kein gültiger Startpunkt gefunden."] };
    }

    const startNodeId = startNodes[0].cellId;

    // 5. Build Smart IDs
    const idMap = new Map();
    const usedIds = new Set();

    validBotNodes.forEach(n => {
        let proposedId = extractSmartId(n.cellValue, `node_${n.cellId}`);
        let finalId = proposedId;
        let counter = 1;
        
        while (usedIds.has(finalId)) {
            finalId = `${proposedId}_${counter}`;
            counter++;
        }
        
        usedIds.add(finalId);
        idMap.set(n.cellId, finalId);
    });

    // 6. Construct the JSON Catalog
    const defaultSupportNodeId = "default-support-node";
    const questionCatalog = {
        categories: labels.map(l => l.cellValue.replace(/kategorie:/i, "").trim()),
        nodes: []
    };

    const defaultSupportNode = {
        id: defaultSupportNodeId,
        label: NODE_OK_DEFAULT_TEXT,
        followUpSteps: { type: "end", steps: [] }
    };

    const sortedBotNodes = [
        validBotNodes.find(n => n.cellId === startNodeId),
        ...validBotNodes.filter(n => n.cellId !== startNodeId)
    ].filter(Boolean);

    sortedBotNodes.forEach(node => {
        const nodeId = idMap.get(node.cellId);
        
        // FIX: If a box is completely empty, give it a placeholder name so the app doesn't crash
        const nodeText = cleanTextForDisplay(node.cellValue) || `[Leerer Knoten: ${nodeId}]`; 
        
        const steps = [];

        node.outEdges.forEach(edge => {
            let input = "Weiter";
            let nextCellId = defaultSupportNodeId;
            
            const edgeText = cleanTextForDisplay(edge.cellValue); 

            const resolvedTargetId = resolveTargetId(edge.target);
            
            if (resolvedTargetId) {
                if (edgeText) {
                    input = edgeText;
                }
                nextCellId = resolvedTargetId;
            }

            const isEnd = input.toLowerCase().includes("ende") || input.toLowerCase().includes("end");
            let nextNodeId = isEnd ? defaultSupportNodeId : (idMap.get(nextCellId) || defaultSupportNodeId);

            if (nextNodeId !== nodeId || edgeText) {
               steps.push({ input, nextNodeId });
            }
        });

        questionCatalog.nodes.push({
            id: nodeId,
            domainIdentifier: nodeId,
            label: nodeText,
            followUpSteps: {
                type: steps.length > 0 ? "question" : "end",
                steps: steps
            }
        });
    });

    questionCatalog.nodes.push(defaultSupportNode);
    return { catalog: questionCatalog };
}
